/**
 * Call audit log, school-admin visible (SCH-614), live-call monitor (SCH-620)
 * and recordings (SCH-619).
 *
 *   GET  /v1/calls/school-admin/history                      school admin, x-school-id
 *   GET  /v1/calls/school-admin/history/:callRecordId        school admin
 *   GET  /v1/calls/school-admin/refusals                     school admin
 *   GET  /v1/calls/school-admin/live                         school admin
 *   POST /v1/calls/school-admin/live/:callRecordId/monitor   school admin
 *   GET  /v1/calls/school-admin/recordings/:recordingId/url  school admin
 *
 * "School admin" is an ACTIVE `UsersOnSchools.isAdmin` at the `x-school-id`
 * school, or a super-admin. Records come from server-authoritative signals: the
 * server-side create (`POST /v1/calls`) and the Stream Video webhook — never a
 * client's report.
 */

import { z } from 'zod'
import { CallRefusalReasonSchema } from './create-call'

export const CallRecordKindSchema = z.enum(['direct', 'group', 'scheduled'])
export type CallRecordKind = z.infer<typeof CallRecordKindSchema>

export const CallRecordStatusSchema = z.enum(['created', 'ringing', 'active', 'ended'])
export type CallRecordStatus = z.infer<typeof CallRecordStatusSchema>

/** R13 outcomes, plus `cancelled` (caller hung up while ringing). Null while the call is live. */
export const CallRecordOutcomeSchema = z.enum(['completed', 'declined', 'missed', 'dropped', 'cancelled'])
export type CallRecordOutcome = z.infer<typeof CallRecordOutcomeSchema>

/** The role a participant held when the call was authorized (snapshot). */
export const CallParticipantRoleSchema = z.enum(['student', 'parent', 'faculty', 'staff', 'admin', 'other'])
export type CallParticipantRole = z.infer<typeof CallParticipantRoleSchema>

export const CallParticipantResponseSchema = z.enum(['none', 'accepted', 'declined'])
export type CallParticipantResponse = z.infer<typeof CallParticipantResponseSchema>

export const CallAuditEventTypeSchema = z.enum([
  'call_authorized',
  'call_refused',
  'ring_sent',
  'session_started',
  'participant_joined',
  'participant_left',
  'call_ended',
  'call_ended_by_policy',
  'member_added',
  'member_refused',
  'monitor_joined',
  'recording_started',
  'recording_ready',
  'recording_deleted'
])
export type CallAuditEventType = z.infer<typeof CallAuditEventTypeSchema>

export const CallPersonRefSchema = z.object({
  /** UsersOnSchools.publicId (the Stream user id). */
  publicId: z.string(),
  displayName: z.string(),
  role: CallParticipantRoleSchema
})
export type CallPersonRef = z.infer<typeof CallPersonRefSchema>

export const CallParticipantSchema = CallPersonRefSchema.extend({
  isInitiator: z.boolean(),
  /** Joined as a school-admin monitor (SCH-620). */
  isMonitor: z.boolean(),
  /** Joined without being authorized; the server ejected them. Never rung, never counted. */
  unauthorized: z.boolean(),
  response: CallParticipantResponseSchema,
  firstJoinedAt: z.string().datetime().nullable(),
  lastLeftAt: z.string().datetime().nullable()
})
export type CallParticipant = z.infer<typeof CallParticipantSchema>

export const CallRecordSummarySchema = z.object({
  id: z.string(),
  callCid: z.string(),
  kind: CallRecordKindSchema,
  video: z.boolean(),
  status: CallRecordStatusSchema,
  outcome: CallRecordOutcomeSchema.nullable(),
  initiator: CallPersonRefSchema,
  participantCount: z.number().int().nonnegative(),
  /** The call was created (server-side create). */
  createdAt: z.string().datetime(),
  /** First participant joined (Stream session started). */
  startedAt: z.string().datetime().nullable(),
  /** A second participant joined — the call connected. */
  connectedAt: z.string().datetime().nullable(),
  endedAt: z.string().datetime().nullable(),
  /** Seconds from connected to ended; null when it never connected. */
  durationSeconds: z.number().int().nonnegative().nullable(),
  recorded: z.boolean(),
  scheduledSessionId: z.string().nullable()
})
export type CallRecordSummary = z.infer<typeof CallRecordSummarySchema>

export const CallAuditEventSchema = z.object({
  id: z.string(),
  type: CallAuditEventTypeSchema,
  at: z.string().datetime(),
  actor: CallPersonRefSchema.nullable(),
  /** Refusal reason for `call_refused` / `member_refused`. */
  reason: CallRefusalReasonSchema.nullable(),
  detail: z.record(z.string(), z.unknown()).nullable()
})
export type CallAuditEvent = z.infer<typeof CallAuditEventSchema>

export const CallRecordingSchema = z.object({
  id: z.string(),
  startedAt: z.string().datetime().nullable(),
  endedAt: z.string().datetime().nullable(),
  /** When the purge job deletes it. */
  expiresAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable()
})
export type CallRecording = z.infer<typeof CallRecordingSchema>

export const CallRecordDetailSchema = CallRecordSummarySchema.extend({
  channelCid: z.string().nullable(),
  participants: z.array(CallParticipantSchema),
  events: z.array(CallAuditEventSchema),
  recordings: z.array(CallRecordingSchema)
})
export type CallRecordDetail = z.infer<typeof CallRecordDetailSchema>

// ---------------------------------------------------------------------------
// GET /v1/calls/school-admin/history
// ---------------------------------------------------------------------------

export const ListCallHistoryRequestSchema = z.object({
  /** Opaque cursor from the previous page. */
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** Only calls one of these people took part in (UsersOnSchools.publicId). */
  participantId: z.string().optional(),
  outcome: CallRecordOutcomeSchema.optional(),
  /** Created at or after. */
  from: z.string().datetime().optional(),
  /** Created before. */
  to: z.string().datetime().optional()
})
export type ListCallHistoryRequest = z.infer<typeof ListCallHistoryRequestSchema>

export const ListCallHistoryResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    calls: z.array(CallRecordSummarySchema),
    nextCursor: z.string().nullable(),
    /** Days records are kept at this school. */
    retentionDays: z.number().int()
  })
})
export type ListCallHistoryResponse = z.infer<typeof ListCallHistoryResponseSchema>

export const CallRecordIdParamsSchema = z.object({ callRecordId: z.string().min(1).max(64) })
export type CallRecordIdParams = z.infer<typeof CallRecordIdParamsSchema>

export const GetCallRecordResponseSchema = z.object({
  message: z.string(),
  data: CallRecordDetailSchema
})
export type GetCallRecordResponse = z.infer<typeof GetCallRecordResponseSchema>

// ---------------------------------------------------------------------------
// GET /v1/calls/school-admin/refusals — refused attempts (no Stream call exists)
// ---------------------------------------------------------------------------

export const CallRefusalRecordSchema = z.object({
  id: z.string(),
  at: z.string().datetime(),
  actor: CallPersonRefSchema.nullable(),
  reason: CallRefusalReasonSchema,
  channelCid: z.string().nullable(),
  /** People the refusal named. */
  targets: z.array(CallPersonRefSchema)
})
export type CallRefusalRecord = z.infer<typeof CallRefusalRecordSchema>

export const ListCallRefusalsResponseSchema = z.object({
  message: z.string(),
  data: z.object({ refusals: z.array(CallRefusalRecordSchema), nextCursor: z.string().nullable() })
})
export type ListCallRefusalsResponse = z.infer<typeof ListCallRefusalsResponseSchema>

// ---------------------------------------------------------------------------
// Live calls + monitor (SCH-620)
// ---------------------------------------------------------------------------

export const LiveCallSchema = CallRecordSummarySchema.extend({
  /** People in the call right now. */
  liveParticipants: z.array(CallPersonRefSchema)
})
export type LiveCall = z.infer<typeof LiveCallSchema>

export const ListLiveCallsResponseSchema = z.object({
  message: z.string(),
  data: z.object({ calls: z.array(LiveCallSchema) })
})
export type ListLiveCallsResponse = z.infer<typeof ListLiveCallsResponseSchema>

/**
 * POST /v1/calls/school-admin/live/:callRecordId/monitor — adds the admin as a
 * visible member of the call and returns what the web client needs to join it
 * (its own Stream Video token for the school's Stream app).
 */
export const MonitorCallResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    callType: z.literal('default'),
    callId: z.string(),
    callCid: z.string(),
    apiKey: z.string(),
    token: z.string(),
    /** The admin's Stream user id. */
    userId: z.string(),
    userName: z.string()
  })
})
export type MonitorCallResponse = z.infer<typeof MonitorCallResponseSchema>

// ---------------------------------------------------------------------------
// Recordings (SCH-619)
// ---------------------------------------------------------------------------

export const CallRecordingIdParamsSchema = z.object({ recordingId: z.string().min(1).max(64) })
export type CallRecordingIdParams = z.infer<typeof CallRecordingIdParamsSchema>

export const GetCallRecordingUrlResponseSchema = z.object({
  message: z.string(),
  data: z.object({ url: z.string().url(), expiresAt: z.string().datetime() })
})
export type GetCallRecordingUrlResponse = z.infer<typeof GetCallRecordingUrlResponseSchema>
